import type {
  CoordinatePlanePayload,
  GeometryPayload,
  NumberLinePayload,
} from '@/components/whiteboard-v2/types';

export type VisualMode = 'coordinate_plane' | 'geometry' | 'number_line' | 'none';

export type VisualPayload = NumberLinePayload | CoordinatePlanePayload | GeometryPayload;

export interface VisualSpec {
  required: boolean;
  mode: VisualMode;
  description?: string;
  payload?: VisualPayload;
}

const TRUE_SET = new Set(['1', 'true', 'yes', 'on']);

function readFlag(rawValue: string | undefined, defaultValue: boolean): boolean {
  if (rawValue === undefined) return defaultValue;
  const normalized = rawValue.trim().toLowerCase();
  if (!normalized) return defaultValue;
  if (TRUE_SET.has(normalized)) return true;
  if (['0', 'false', 'no', 'off'].includes(normalized)) return false;
  return defaultValue;
}

export function isVisualPromptV2Enabled(): boolean {
  const raw = process.env.ENABLE_VISUAL_PROMPT_V2 ?? process.env.NEXT_PUBLIC_ENABLE_VISUAL_PROMPT_V2;
  return readFlag(raw, true);
}

export function isVisualRenderEnabled(): boolean {
  const raw = process.env.NEXT_PUBLIC_ENABLE_VISUAL_RENDER ?? process.env.ENABLE_VISUAL_RENDER;
  return readFlag(raw, true);
}

export function isVisualBackfillEnabled(): boolean {
  const raw = process.env.ENABLE_VISUAL_BACKFILL;
  return readFlag(raw, false);
}

function parseMaybeJson(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const text = value.trim();
  if (!text) return value;
  if (!text.startsWith('{') && !text.startsWith('[')) return value;

  try {
    return JSON.parse(text);
  } catch {
    return value;
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  const parsed = parseMaybeJson(value);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }
  return parsed as Record<string, unknown>;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

function asBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return undefined;
  if (TRUE_SET.has(normalized)) return true;
  if (['0', 'false', 'no', 'off'].includes(normalized)) return false;
  return undefined;
}

function asMode(value: unknown): VisualMode | undefined {
  if (typeof value !== 'string') return undefined;

  const normalized = value.trim().toLowerCase();
  if (
    normalized === 'coordinate_plane'
    || normalized === 'geometry'
    || normalized === 'number_line'
    || normalized === 'none'
  ) {
    return normalized;
  }

  return undefined;
}

function asPayload(value: unknown): VisualPayload | undefined {
  const record = asRecord(value);
  if (!record) return undefined;
  return record as unknown as VisualPayload;
}

export function parseVisualSpec(value: unknown): VisualSpec | null {
  const record = asRecord(value);
  if (!record) return null;

  const mode = asMode(record.mode) ?? 'none';
  const required = asBoolean(record.required) ?? false;
  const description = asString(record.description);
  const payload = asPayload(record.payload);

  if (mode === 'none' && !description && !payload) {
    return null;
  }

  return {
    required,
    mode,
    ...(description ? { description } : {}),
    ...(payload ? { payload } : {}),
  };
}

export function getQuestionVisual(question: unknown): VisualSpec | null {
  const record = asRecord(question);
  if (!record) return null;

  const metadata = asRecord(record.metadata);
  const fromMetadata = parseVisualSpec(metadata?.visual);
  if (fromMetadata) return fromMetadata;

  return parseVisualSpec(record.visual);
}

export function getKeyPropertyVisual(keyPropertyLike: unknown): VisualSpec | null {
  const record = asRecord(keyPropertyLike);
  if (!record) return null;

  const nestedKeyProperty = asRecord(record.key_property);
  if (nestedKeyProperty) {
    const nestedVisual = parseVisualSpec(nestedKeyProperty.visual);
    if (nestedVisual) return nestedVisual;
  }

  return parseVisualSpec(record.visual);
}

export function getQuestionPictureFallback(question: unknown): string | undefined {
  const record = asRecord(question);
  if (!record) return undefined;

  const metadata = asRecord(record.metadata);
  if (metadata) {
    if (Array.isArray(metadata.pictures)) {
      const first = metadata.pictures.find((item) => typeof item === 'string' && item.trim().length > 0);
      if (typeof first === 'string') {
        return first.trim();
      }
    }

    const metadataPicture = asString(metadata.picture);
    if (metadataPicture) return metadataPicture;
  }

  return asString(record.picture);
}
